import React from "react";

import cx from "classnames";

import { ThemeContextProvider } from "~/common/ThemeContext";
import { AuthContext } from "~/lib/auth";
import { dispatchAnalyticsPage } from "~/lib/segment";
import DocumentTitle from "~/lib/title";
import { isTeams, simpleHash } from "~/lib/utils";

import { MobileHeader } from "./components";
import type { PaneProps } from "./panes/types";
import WhitelabelPane from "./panes/WhitelabelPane";
import type { AuthProps } from "./types";

import styles from "./css/Auth.module.css";

export class AuthBase<
	P extends AuthProps = AuthProps,
	S extends { errorMessage: string | undefined } = { errorMessage: undefined },
> extends React.Component<P, S> {
	documentTitle = "Auth";

	static contextType = AuthContext;
	declare context: React.ContextType<typeof AuthContext>;

	componentDidMount() {
		dispatchAnalyticsPage("Authentication", this.documentTitle);

		if (this.context.user?.email) {
			// window.heap?.identify(this.context.user.email);
			window.ga?.("set", "userId", simpleHash(this.context.user.email));
		}
	}

	getPaneComponent(): (props: PaneProps) => React.ReactElement {
		return WhitelabelPane;
	}

	getPaneProps(): PaneProps {
		return {};
	}

	renderStagingAppError() {
		return (
			<div className={styles.stagingAppError}>
				⛔ THIS IS A TEST COPY OF FELLOW.APP, NOT INTENDED FOR PUBLIC USE. ⛔
				<br />⛔ YOUR DATA WILL BE DELETED WITHOUT WARNING! ⛔
			</div>
		);
	}

	renderHeader(): React.ReactNode {
		if (this.state.errorMessage) {
			return (
				<div data-test-id="auth-error-message" className={styles.authError}>
					{this.state.errorMessage}
				</div>
			);
		}

		if (window.ENVIRONMENT === "staging" || window.ENVIRONMENT === "sandbox") {
			return this.renderStagingAppError();
		}

		return null;
	}

	renderStep(): React.ReactNode {
		return <div />;
	}

	renderFooter(): React.ReactNode {
		return <div />;
	}

	navigateTo(location: string) {
		return () => this.props.history.push(location);
	}

	onBackClick = () => this.props.history.goBack();

	getMobileHeaderComponent() {
		return MobileHeader;
	}

	render() {
		const PaneComponent = this.getPaneComponent();
		const MobileHeaderComponent = this.getMobileHeaderComponent();
		const isSignupPage = ["/auth/signup/", "/auth/signup/join/"].includes(this.props.location.pathname);

		return (
			<ThemeContextProvider theme="LIGHT">
				<div className={cx(styles.auth, { [styles.authTeams]: isTeams })}>
					<DocumentTitle title={this.documentTitle} />
					<PaneComponent {...this.getPaneProps()} />
					{!isSignupPage && <MobileHeaderComponent {...this.getPaneProps()} />}

					<div className={cx(styles.authStep, { [styles.authStepSignup]: isSignupPage })}>
						<div className={styles.authStepHeader}>{this.renderHeader()}</div>
						<div className={styles.authStepBody}>{this.renderStep()}</div>
						<div className={styles.authStepFooter}>{this.renderFooter()}</div>
					</div>
				</div>
			</ThemeContextProvider>
		);
	}
}
